// Overlay + panel + ESC + click-outside to close.
//
// Responsive by default: a bottom-anchored sheet on phones, a centered dialog
// from `sm` up. Mobile v3 specifies sheets, and the reason is physical — a
// centered dialog puts its controls in the middle of a screen held
// one-handed, where the thumb does not reach. A sheet puts them at the
// bottom.
//
// `variant` forces one or the other where a surface needs it; `responsive` is
// the default and is what nearly every call site wants.

import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  /** 'responsive' (default) = sheet on phones, dialog on desktop. */
  variant?: 'responsive' | 'sheet' | 'dialog';
  footer?: ReactNode;
  children: ReactNode;
}

// Width caps apply from `sm` up. Below that the panel is full-bleed, because
// a sheet inset from the screen edge is neither a sheet nor a dialog.
const WIDTHS = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
};

export function Modal({
  open, onClose, title, subtitle, width = 'md', variant = 'responsive', footer, children,
}: Props) {
  // ESC closes; the page behind stops scrolling.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const sheet = variant === 'sheet';
  const dialog = variant === 'dialog';

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex bg-[var(--color-ink)]/40 backdrop-blur-sm',
        dialog ? 'items-center justify-center p-4'
          : sheet ? 'items-end justify-center'
          : 'items-end justify-center sm:items-center sm:p-4',
      )}
      onClick={onClose}
    >
      <div
        className={cn(
          'flex w-full flex-col border border-[var(--color-border)] bg-white shadow-xl',
          WIDTHS[width],
          dialog ? 'max-h-[90vh] rounded-2xl'
            // 26px top corners, 80% max height, per Mobile v3. Bottom corners
            // stay square — the sheet is flush with the screen edge.
            : sheet ? 'max-h-[80vh] rounded-t-[26px]'
            : 'max-h-[80vh] rounded-t-[26px] sm:max-h-[90vh] sm:rounded-2xl',
        )}
        // A sheet sits against the iOS home indicator; without this its last
        // control lands under the system gesture bar.
        style={dialog ? undefined : { paddingBottom: 'env(safe-area-inset-bottom)' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Grab handle — an affordance saying "this came from the bottom and
            goes back there". Hidden from assistive tech, and from the desktop
            layout where the panel is centred. */}
        {!dialog && (
          <div className={cn('flex justify-center pt-2.5 pb-1', !sheet && 'sm:hidden')} aria-hidden="true">
            <span className="h-[5px] w-10 rounded-[3px] bg-[var(--color-border)]" />
          </div>
        )}

        {(title || subtitle) && (
          <div className="flex items-start justify-between gap-3 border-b border-[var(--color-hairline)] px-5 py-4">
            <div className="min-w-0">
              {title && <h2 className="truncate text-[16px] font-semibold tracking-tight text-[var(--color-ink)]">{title}</h2>}
              {subtitle && <p className="mt-0.5 truncate text-[12px] text-[var(--color-secondary-2)]">{subtitle}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-md p-1.5 text-[var(--color-secondary-2)] hover:bg-[var(--color-panel)]"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-[var(--color-hairline)] bg-[var(--color-panel)] px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
